/**
 * vibecheck docguard — Documentation health and coverage checker
 */

import * as fs from 'fs';
import * as path from 'path';
import type { Command } from 'commander';
import ora from 'ora';
import type { GlobalOptions } from '../types';
import { walkFiles, readFileContent } from '../core/file-walker';
import { detectProject } from '../core/project-detect';
import * as out from '../ui/output';
import { colors, icons } from '../ui/theme';

export function registerDocguard(program: Command): void {
  const cmd = program
    .command('docguard')
    .description('Documentation health and coverage checker')
    .option('--format <type>', 'Output format')
    .option('--include <patterns>', 'Files to check')
    .option('--output <path>', 'Output directory for generated docs')
    .option('--style <type>', 'Doc style: jsdoc, tsdoc, markdown', 'tsdoc');

  cmd.command('check').description('Check docs for staleness and accuracy (default)').action(async (_o: any, c: Command) => {
    await runCheck({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any) });
  });

  cmd.command('coverage').description('Show documentation coverage stats').action(async (_o: any, c: Command) => {
    await runCoverage({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any) });
  });

  cmd.command('generate <path>').description('Generate docs for a file/module').action(async (target: string, _o: any, c: Command) => {
    await runGenerate({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any) }, target);
  });

  cmd.command('sync').description('Update docs to match current code').action(async (_o: any, c: Command) => {
    await runSync({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any) });
  });

  cmd.action(async (opts: any, command: Command) => {
    await runCheck({ ...(command.parent!.opts() as GlobalOptions), ...opts });
  });
}

interface DocguardOpts extends GlobalOptions {
  format?: string;
  include?: string;
  output?: string;
  style?: string;
}

interface CoverageArea {
  area: string;
  total: number;
  documented: number;
  coverage: number;
}

async function runCheck(opts: DocguardOpts): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();

  out.brandHeader('DocGuard', 'Documentation Health');
  out.nl();

  const spinner = ora('Analyzing documentation...').start();
  const project = detectProject(rootPath);
  const files = await walkFiles(rootPath, { maxFiles: 500 });

  const staleIssues: { file: string; issue: string }[] = [];
  const missingDocs: { file: string; count: number; type: string }[] = [];

  // Check README.md accuracy
  const readmePath = path.join(rootPath, 'README.md');
  if (fs.existsSync(readmePath)) {
    const readme = fs.readFileSync(readmePath, 'utf-8');

    // Check for stale package manager references
    if (project.packageManager === 'pnpm' && readme.includes('yarn start') && !readme.includes('pnpm')) {
      staleIssues.push({ file: 'README.md', issue: `References 'yarn start' but project uses ${project.packageManager}` });
    }
    if (project.packageManager === 'pnpm' && readme.includes('npm run') && !readme.includes('pnpm')) {
      staleIssues.push({ file: 'README.md', issue: `References 'npm run' but project uses ${project.packageManager}` });
    }

    // Check for broken relative links
    const linkMatches = readme.matchAll(/\[([^\]]+)\]\(([^)]+)\)/g);
    for (const match of linkMatches) {
      const linkPath = match[2];
      if (linkPath.startsWith('./') || linkPath.startsWith('../')) {
        const resolved = path.resolve(rootPath, linkPath);
        if (!fs.existsSync(resolved)) {
          staleIssues.push({ file: 'README.md', issue: `Broken link: [${match[1]}](${linkPath}) — file not found` });
        }
      }
    }
  }

  // Check CHANGELOG freshness
  const changelogPath = path.join(rootPath, 'CHANGELOG.md');
  if (fs.existsSync(changelogPath)) {
    const changelog = fs.readFileSync(changelogPath, 'utf-8');
    // Check if changelog has recent date (within last 90 days)
    const datePattern = /\d{4}-\d{2}-\d{2}/g;
    const dates = changelog.match(datePattern);
    if (dates && dates.length > 0) {
      const latestDate = new Date(dates[0]);
      const daysSince = Math.floor((Date.now() - latestDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSince > 90) {
        staleIssues.push({ file: 'CHANGELOG.md', issue: `Last entry is ${daysSince} days old — may be stale` });
      }
    }
    // Check if changelog mentions current version from package.json
    try {
      const pkg = JSON.parse(fs.readFileSync(path.join(rootPath, 'package.json'), 'utf-8'));
      if (pkg.version && !changelog.includes(pkg.version)) {
        staleIssues.push({ file: 'CHANGELOG.md', issue: `Current version ${pkg.version} not found in CHANGELOG` });
      }
    } catch { /* ignore */ }
  }

  // Check for license headers in source files
  const licenseHeaderIssues: { file: string; type: string }[] = [];

  // Check for undocumented exports
  let totalExports = 0;
  let documentedExports = 0;

  for (const file of files) {
    const ext = path.extname(file);
    if (!['.ts', '.tsx', '.js', '.jsx'].includes(ext)) continue;

    const content = readFileContent(rootPath, file);
    if (!content) continue;

    const lines = content.split('\n');
    let undocumented = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Check for exported functions/classes without JSDoc
      if (/^export\s+(async\s+)?function\s+\w+/.test(line) || /^export\s+class\s+\w+/.test(line) || /^export\s+const\s+\w+/.test(line)) {
        totalExports++;
        // Check if previous lines have JSDoc
        const prevLines = lines.slice(Math.max(0, i - 5), i).join('\n');
        if (prevLines.includes('/**') && prevLines.includes('*/')) {
          documentedExports++;
        } else {
          undocumented++;
        }
      }
    }

    if (undocumented > 0) {
      const isComponent = file.includes('component') || ext === '.tsx';
      const isHook = file.includes('hook') || file.includes('use');
      const isUtil = file.includes('util') || file.includes('helper') || file.includes('lib');
      const type = isComponent ? 'component' : isHook ? 'hook' : isUtil ? 'utility' : 'module';
      missingDocs.push({ file, count: undocumented, type });
    }

    // Check for deprecated JSDoc tags that reference non-existent functions
    const deprecatedRefs = content.match(/@see\s+(\w+)/g);
    if (deprecatedRefs) {
      for (const ref of deprecatedRefs) {
        const funcName = ref.replace('@see ', '').trim();
        if (!content.includes(`function ${funcName}`) && !content.includes(`const ${funcName}`)) {
          staleIssues.push({ file, issue: `@see reference to '${funcName}' — function not found in file` });
        }
      }
    }
  }

  spinner.stop();
  const duration = performance.now() - start;

  // Calculate coverage by area
  const areas: CoverageArea[] = [];
  const areaMap: Record<string, { total: number; documented: number }> = {};

  for (const file of files) {
    const ext = path.extname(file);
    if (!['.ts', '.tsx', '.js', '.jsx'].includes(ext)) continue;

    let area = 'Other';
    if (file.includes('/api/') || file.includes('/routes/')) area = 'API Routes';
    else if (file.includes('/component') || ext === '.tsx') area = 'Components';
    else if (file.includes('/util') || file.includes('/helper') || file.includes('/lib/')) area = 'Utilities';
    else if (file.includes('/type') || file.includes('.d.ts')) area = 'Types/Interfaces';
    else if (file.includes('/hook') || file.includes('use')) area = 'Hooks';

    if (!areaMap[area]) areaMap[area] = { total: 0, documented: 0 };

    const content = readFileContent(rootPath, file);
    if (!content) continue;

    const exportMatches = content.match(/^export\s+(async\s+)?(?:function|class|const|interface|type)\s+/gm);
    if (exportMatches) {
      areaMap[area].total += exportMatches.length;
      // Simple heuristic: count JSDoc blocks
      const jsdocCount = (content.match(/\/\*\*[\s\S]*?\*\//g) || []).length;
      areaMap[area].documented += Math.min(jsdocCount, exportMatches.length);
    }
  }

  for (const [area, data] of Object.entries(areaMap)) {
    if (data.total > 0) {
      areas.push({
        area,
        total: data.total,
        documented: data.documented,
        coverage: Math.round((data.documented / data.total) * 100),
      });
    }
  }

  const overallCoverage = totalExports > 0 ? Math.round((documentedExports / totalExports) * 100) : 0;

  if (opts.json) {
    out.jsonOutput({ overallCoverage, areas, staleIssues, missingDocs, duration });
    return;
  }

  // Pretty output
  console.log(`Documentation coverage: ${overallCoverage >= 80 ? colors.success(`${overallCoverage}%`) : overallCoverage >= 50 ? colors.warning(`${overallCoverage}%`) : colors.error(`${overallCoverage}%`)} ${overallCoverage < 60 ? colors.dim('(needs improvement)') : ''}`);
  out.nl();

  // Coverage table
  if (areas.length > 0) {
    console.log(`  ${colors.bold('Coverage by area:')}`);
    console.log(out.table(
      ['Area', 'Coverage', 'Status'],
      areas.map(a => [
        a.area,
        `${a.coverage}%`,
        a.coverage >= 80 ? colors.success(icons.success) : a.coverage >= 50 ? colors.warning(icons.warning) : colors.error(icons.error),
      ])
    ));
    out.nl();
  }

  // Stale docs
  if (staleIssues.length > 0) {
    console.log(`  ${colors.error('Stale docs found:')}`);
    for (const issue of staleIssues) {
      console.log(`     ${issue.file} — ${issue.issue}`);
    }
    out.nl();
  }

  // Missing docs
  const totalMissing = missingDocs.reduce((sum, m) => sum + m.count, 0);
  if (totalMissing > 0) {
    console.log(`  ${colors.warning('Missing docs:')}`);
    // Group by type
    const byType: Record<string, number> = {};
    for (const m of missingDocs) {
      byType[m.type] = (byType[m.type] || 0) + m.count;
    }
    for (const [type, count] of Object.entries(byType)) {
      console.log(`     ${count} exported ${type}${count > 1 ? 's' : ''} have no JSDoc`);
    }
    out.nl();
  }

  // Changelog status
  if (!fs.existsSync(changelogPath)) {
    console.log(`  ${colors.warning('No CHANGELOG.md found')} — consider adding one for release tracking`);
    out.nl();
  }

  console.log(out.divider());
  if (totalMissing > 0) {
    console.log(`Run ${colors.info('vibecheck docguard generate src/')} to auto-generate.`);
    console.log(`Run ${colors.info('vibecheck docguard sync')} to fix stale docs.`);
  } else if (overallCoverage === 0 && totalExports === 0) {
    console.log(colors.dim('  No documented exports detected. Run on a src/ directory for detailed coverage.'));
  } else if (overallCoverage < 50) {
    console.log(`  Coverage is low at ${overallCoverage}%. Run ${colors.info('vibecheck docguard generate src/')} to improve.`);
  } else {
    out.success('Documentation is in great shape!');
  }
  out.nl();
  console.log(out.completedIn(duration));
}

async function runCoverage(opts: DocguardOpts): Promise<void> {
  // Delegates to check with coverage focus
  await runCheck(opts);
}

async function runGenerate(opts: DocguardOpts, target: string): Promise<void> {
  const rootPath = opts.path || process.cwd();

  out.brandHeader('DocGuard', 'Generate documentation');
  out.nl();

  const files = await walkFiles(rootPath, { include: [`${target}/**/*.{ts,tsx,js,jsx}`] });
  if (files.length === 0) {
    out.warn(`No source files found in ${target}`);
    return;
  }

  let generated = 0;
  for (const file of files) {
    const content = readFileContent(rootPath, file);
    if (!content) continue;

    const lines = content.split('\n');
    let modified = false;
    const newLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      // Check for exported functions without JSDoc
      const funcMatch = trimmed.match(/^export\s+(async\s+)?function\s+(\w+)\s*\(([^)]*)\)/);
      const classMatch = trimmed.match(/^export\s+class\s+(\w+)/);

      if (funcMatch || classMatch) {
        // Check if already has JSDoc
        const prevLines = newLines.slice(-5).join('\n');
        if (!prevLines.includes('/**')) {
          const indent = line.match(/^(\s*)/)?.[1] || '';
          if (funcMatch) {
            const name = funcMatch[2];
            const params = funcMatch[3].split(',').map(p => p.trim().split(':')[0].trim()).filter(Boolean);
            newLines.push(`${indent}/**`);
            newLines.push(`${indent} * ${name}`);
            for (const param of params) {
              if (param && param !== '') {
                newLines.push(`${indent} * @param ${param}`);
              }
            }
            newLines.push(`${indent} */`);
            modified = true;
            generated++;
          } else if (classMatch) {
            newLines.push(`${indent}/**`);
            newLines.push(`${indent} * ${classMatch[1]}`);
            newLines.push(`${indent} */`);
            modified = true;
            generated++;
          }
        }
      }

      newLines.push(line);
    }

    if (modified) {
      const absPath = path.resolve(rootPath, file);
      fs.writeFileSync(absPath, newLines.join('\n'), 'utf-8');
      console.log(`  ${colors.success(icons.file)} Updated: ${file}`);
    }
  }

  out.nl();
  if (generated > 0) {
    out.success(`Generated ${generated} JSDoc comment${generated > 1 ? 's' : ''} across ${files.length} files`);
  } else {
    out.info('All exports already have documentation.');
  }
  out.nl();
}

async function runSync(opts: DocguardOpts): Promise<void> {
  const rootPath = opts.path || process.cwd();

  out.brandHeader('DocGuard', 'Sync documentation');
  out.nl();

  const project = detectProject(rootPath);
  let fixes = 0;

  // Fix README package manager references
  const readmePath = path.join(rootPath, 'README.md');
  if (fs.existsSync(readmePath)) {
    let readme = fs.readFileSync(readmePath, 'utf-8');
    const pm = project.packageManager;

    if (pm === 'pnpm') {
      if (readme.includes('npm run ') || readme.includes('npm install')) {
        readme = readme.replace(/npm run /g, 'pnpm ').replace(/npm install/g, 'pnpm install');
        fixes++;
      }
      if (readme.includes('yarn ') && !readme.includes('pnpm')) {
        readme = readme.replace(/yarn start/g, 'pnpm start').replace(/yarn install/g, 'pnpm install');
        fixes++;
      }
    }

    if (fixes > 0) {
      fs.writeFileSync(readmePath, readme, 'utf-8');
      console.log(`  ${colors.success(icons.success)} Fixed ${fixes} stale reference${fixes > 1 ? 's' : ''} in README.md`);
    }
  }

  if (fixes === 0) {
    out.info('No stale docs found to fix.');
  }
  out.nl();
}
